import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahgojjoh653ivfvnuqg";

const MAX_MESSAGE = 500;

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      setMessage("");
      return;
    }
    payload.delete("contact_alt");

    if (message.length > MAX_MESSAGE) {
      setStatus("error");
      setFormError(`内容不能超过 ${MAX_MESSAGE} 个字符。`);
      return;
    }

    setStatus("loading");
    setFormError("");

    const body = new URLSearchParams();
    payload.forEach((value, key) => body.append(key, String(value)));

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }
      const meta =
        parsed && typeof parsed.meta === "object" && parsed.meta !== null
          ? (parsed.meta as Record<string, unknown>)
          : undefined;
      const serverMsg = String(
        meta?.message ?? parsed?.message ?? meta?.detail ?? responseText ?? "",
      );
      const code = parsed ? String(parsed.code ?? "") : "";
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
        setMessage("");
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试。");
    }
  };

  return (
    <form data-readdy-form id="contact-form" onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block text-sm text-foreground-700">
            你的称呼
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼你"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1.5 block text-sm text-foreground-700">
            邮箱地址
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="方便我们回复你"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </div>

      <div>
        <label htmlFor="contact-topic" className="mb-1.5 block text-sm text-foreground-700">
          主题
        </label>
        <select
          id="contact-topic"
          name="topic"
          defaultValue="投稿线索"
          className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
        >
          <option value="投稿线索">投稿线索</option>
          <option value="内容纠错">内容纠错</option>
          <option value="合作沟通">合作沟通</option>
          <option value="其他建议">其他建议</option>
        </select>
      </div>

      <div>
        <label htmlFor="contact-message" className="mb-1.5 block text-sm text-foreground-700">
          想说的话
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={5}
          maxLength={MAX_MESSAGE}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="简单写明事情的来龙去脉，以及可以核实的来源"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
        />
        <p className="mt-1 text-right text-xs text-foreground-400">
          {message.length} / {MAX_MESSAGE}
        </p>
      </div>

      <input
        className="form-honeypot"
        type="text"
        name="contact_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <button
        type="submit"
        disabled={status === "loading"}
        className="w-full whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
      >
        {status === "loading" ? "提交中…" : "发送给我们"}
      </button>

      {status === "success" && (
        <p className="rounded-md bg-accent-100/70 px-4 py-3 text-sm text-accent-900">
          已收到你的消息，我们会在核实后尽快回复。
        </p>
      )}
      {status === "error" && (
        <p className="rounded-md bg-primary-100/70 px-4 py-3 text-sm text-primary-800">{formError}</p>
      )}
    </form>
  );
}